import { ChevronDown, ChevronUp, Loader2, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

/** Espera depois da última tecla antes de buscar. */
const ESPERA_DA_DIGITACAO_MS = 350;

/** `.icon-btn` do design system, com o estado desabilitado das setas. */
const BOTAO_DE_ICONE =
  'flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-sm text-n-500 transition-colors hover:bg-n-100 hover:text-n-800 disabled:cursor-default disabled:text-n-300 disabled:hover:bg-transparent';

/** Mínimo de letras pra buscar (o servidor recusa menos). */
export const MINIMO_DA_BUSCA = 2;

interface Props {
  /** Resultado atual: 0 = o mais recente. */
  indice: number;
  /** null = ainda não buscou (termo curto ou primeira digitação). */
  total: number | null;
  /** Passou do teto de resultados — o contador mostra "500+". */
  truncado: boolean;
  /** Buscando no servidor ou descendo a conversa até a mensagem. */
  ocupado: boolean;
  /** Termo já com a espera da digitação; vazio = curto demais. */
  onTermo: (termo: string) => void;
  onMaisAntiga: () => void;
  onMaisRecente: () => void;
  onFechar: () => void;
}

/**
 * Barra "Buscar na conversa", flutuando no topo da conversa. O texto digitado
 * fica SÓ aqui dentro: o painel da conversa só fica sabendo do termo depois da
 * espera — senão cada tecla redesenharia todas as bolhas carregadas.
 *
 * Navegação de cima pra baixo como a própria conversa: ↑ vai pra mensagem
 * achada mais antiga (que está mais acima), ↓ pra mais recente. Enter = ↑,
 * Shift+Enter = ↓, Esc fecha.
 *
 * Visual = campo de busca do design system (`.input-search` do _shared.css,
 * o mesmo do `<Input>`): cantos 10px, borda n-300; no foco,
 * borda brand + anel suave no CAMPO inteiro. O <input> de dentro anula o
 * contorno global de `*:focus-visible` — senão aparece um retângulo azul só
 * em volta do texto, dentro do campo arredondado. Setas e X = `.icon-btn`.
 */
export function BuscaNaConversa({
  indice,
  total,
  truncado,
  ocupado,
  onTermo,
  onMaisAntiga,
  onMaisRecente,
  onFechar,
}: Props) {
  const [texto, setTexto] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const onTermoRef = useRef(onTermo);
  onTermoRef.current = onTermo;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc cancela a busca (igual ao X) com o foco em qualquer lugar — depois de
  // clicar numa seta o foco sai do campo, e o Esc do campo não pegava mais.
  // O do próprio campo chega antes e marca o evento (preventDefault), então
  // não fecha duas vezes. Esc dentro de um modal aberto é do modal.
  const onFecharRef = useRef(onFechar);
  onFecharRef.current = onFechar;
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      const foco = document.activeElement;
      if (foco?.closest('[role="dialog"], [aria-modal="true"]')) return;
      onFecharRef.current();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  useEffect(() => {
    const termo = texto.trim();
    const t = setTimeout(
      () => onTermoRef.current(termo.length >= MINIMO_DA_BUSCA ? termo : ''),
      ESPERA_DA_DIGITACAO_MS,
    );
    return () => clearTimeout(t);
  }, [texto]);

  // A barra nasce só com o campo; setas e contador entram junto com o
  // RESULTADO (achou ou 0/0), nunca antes — na espera da digitação e durante a
  // busca eles apareciam vazios. Enquanto busca, quem avisa é o indicador
  // dentro do campo (mesmo padrão da busca da lista de atendimentos).
  // Termo editado depois de um resultado: o anterior fica na tela até o novo
  // chegar (sem piscar), com as setas travadas enquanto busca.
  const mostrarComandos = texto.trim().length >= MINIMO_DA_BUSCA && total !== null;
  const temResultado = total !== null && total > 0;
  const podeAntiga = !ocupado && temResultado && indice < (total ?? 0) - 1;
  const podeRecente = !ocupado && temResultado && indice > 0;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Composição de acento em andamento (teclado PT-BR) — vide lição do Composer.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      onFechar();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        if (podeRecente) onMaisRecente();
      } else if (podeAntiga) {
        onMaisAntiga();
      }
    }
  };

  return (
    <div className="-translate-x-1/2 absolute top-3 left-1/2 z-20 flex w-[min(430px,calc(100%-32px))] items-center gap-[6px] rounded-lg border border-n-200 bg-n-0 p-[12px] shadow-lg">
      <div className="flex h-[38px] min-w-0 flex-1 items-center gap-[8px] rounded-md border border-n-300 bg-n-0 pr-[6px] pl-[12px] transition-[border-color,box-shadow] focus-within:border-brand-500 focus-within:[box-shadow:var(--ring-brand)]">
        <Search size={16} strokeWidth={1.75} className="flex-shrink-0 text-n-400" />
        <input
          ref={inputRef}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Buscar na conversa"
          aria-label="Buscar na conversa"
          maxLength={100}
          className="min-w-0 flex-1 bg-transparent text-[14px] text-n-900 outline-none placeholder:text-n-400 focus-visible:outline-none"
        />
        {ocupado && (
          <Loader2
            size={16}
            aria-label="Buscando"
            className="flex-shrink-0 animate-spin text-n-400"
          />
        )}
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar busca"
          title="Fechar busca (Esc)"
          className="flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-sm text-n-500 transition-colors hover:bg-danger-50 hover:text-danger-600"
        >
          <X size={16} strokeWidth={1.75} />
        </button>
      </div>
      {mostrarComandos && (
        // Setas e contador colados num grupo só (sem o espaçamento da barra):
        // o número cresce com o conteúdo ("12/500+") em vez de ter largura fixa.
        <div className="flex flex-shrink-0 items-center">
          <button
            type="button"
            onClick={onMaisAntiga}
            disabled={!podeAntiga}
            aria-label="Mensagem mais antiga"
            title="Mais antiga (Enter)"
            className={BOTAO_DE_ICONE}
          >
            <ChevronUp size={20} strokeWidth={1.75} />
          </button>
          <span
            className={`flex min-w-[30px] justify-center px-[2px] font-medium text-[13px] tabular-nums ${
              temResultado ? 'text-n-600' : 'text-n-400'
            }`}
            aria-live="polite"
          >
            {total === 0 ? '0/0' : `${indice + 1}/${total}${truncado ? '+' : ''}`}
          </span>
          <button
            type="button"
            onClick={onMaisRecente}
            disabled={!podeRecente}
            aria-label="Mensagem mais recente"
            title="Mais recente (Shift+Enter)"
            className={BOTAO_DE_ICONE}
          >
            <ChevronDown size={20} strokeWidth={1.75} />
          </button>
        </div>
      )}
    </div>
  );
}
